import { Seo } from "../components/Seo";
import { useGoToSection } from "../lib/useGo";
import { SUPPORT_ITEMS } from "../components/data/content";
import Reveal from "../components/Reveal";
import Footer from "../components/Footer";
import StickyCTA from "../components/StickyCTA";
import {
  FileText, ClipboardList, PenTool, Landmark, ShieldCheck, BadgeCheck, Palette,
  MonitorSmartphone, BarChart3, Receipt, GraduationCap, Compass, Sparkles, Building2,
} from "lucide-react";

const ICONS = {
  FileText, ClipboardList, PenTool, Landmark, ShieldCheck, BadgeCheck, Palette,
  MonitorSmartphone, BarChart3, Receipt, GraduationCap, Compass, Sparkles, Building2,
};

const BusinessSupport = () => {
  const go = useGoToSection();
  return (
    <>
      <Seo
        title="Real Estate Business Support for Agents | Agency HQ"
        description="Real estate business support Australia-wide: trust accounting, compliance, listing administration, marketing, CRM and portals — handled by Agency HQ while you focus on clients."
        path="/real-estate-business-support"
      />
      <main className="pt-[72px]">
        <section className="relative overflow-hidden py-14 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -top-40 right-1/4 h-[500px] w-[500px] rounded-full bg-amber-600/15 blur-[140px]" aria-hidden="true" />
          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <p className="label-mono">Real estate business support</p>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight leading-tight text-white sm:text-4xl lg:text-5xl" data-testid="support-h1">
              Real estate business support for agents, teams and agencies.
            </h1>
            <p className="mt-7 text-base leading-relaxed text-slate-400 sm:text-lg">
              Running an agency means running an office: contracts, trust accounting, compliance, forms,
              invoices, portals, marketing and people. Agency HQ is the central operating platform that
              does that work for you — for your own brand, co-branded with Agency HQ or under the Agency
              HQ name, Australia-wide.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              You focus on relationships, listings and sales. Our experienced team manages everything
              behind the transaction.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a href="/#contact" onClick={go("contact", "seo-support")} data-testid="support-primary-cta-btn" className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25 transition-all duration-200 hover:from-amber-600 hover:to-amber-700">
                Book a confidential conversation
              </a>
              <a href="/#how" onClick={go("how", "seo-support")} data-testid="support-secondary-cta-btn" className="inline-flex items-center justify-center rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-gold/50 hover:text-gold-light">
                How it works
              </a>
            </div>
          </div>
        </section>

        <section className="border-y border-white/10 bg-surface/50 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="max-w-3xl">
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                We take care of the work that takes you away from clients.
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {SUPPORT_ITEMS.map((item, i) => {
                const Icon = ICONS[item.icon];
                return (
                  <Reveal key={item.title} delay={Math.min(i * 0.03, 0.3)} className="h-full">
                    <div className="flex h-full items-start gap-4 rounded-xl border border-white/10 bg-panel/50 p-5">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10">
                        <Icon className="h-[18px] w-[18px] text-gold-light" />
                      </span>
                      <p className="pt-1.5 text-sm font-medium leading-snug text-slate-200">{item.title}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                A back office without the payroll.
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Employing a full administration team costs a salary bill, systems and management — before
                a single sale. Agency HQ members get the same capability from our operations, finance,
                client success, marketing and property management teams, included in one low monthly
                administration membership from $250. There are no establishment costs and no
                franchise-style fees on your commissions.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 rounded-2xl border border-gold/40 bg-gold/[0.06] p-7 text-center">
                <p className="font-display text-xl font-bold text-white">Let&rsquo;s talk about your business.</p>
                <p className="mt-2 text-sm text-slate-400">Confidential, no obligation — and never shared with your current agency.</p>
                <a href="/#contact" onClick={go("contact", "seo-support")} className="mt-5 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3.5 text-sm font-semibold text-slate-950 shadow-lg shadow-amber-500/25">
                  Book a confidential conversation
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCTA />
    </>
  );
};

export default BusinessSupport;
